/**
 * LongPostChunkEditor Component
 * 
 * Modal dialog component for creating and editing long post chunks including:
 * - Add new chunk dialog
 * - Edit existing chunk functionality
 * - Text content editor with formatting options
 * - Chunk metadata management (order, table of contents visibility)
 * - Review mode for viewing chunk content
 * - Integration with text creation and editing APIs
 * - Form validation and error handling
 * - Responsive modal design
 * 
 * Used in: LongPostChunkManager for chunk editing operations
 */
import React, { useState } from 'react';
import {
    Dialog,
    DialogTitle,
    DialogContent,
    DialogActions,
    Button,
    TextField,
    FormControlLabel,
    Checkbox,
    Typography,
    Box,
    IconButton
} from '@mui/material';
import {
    Save,
    Close,
    Edit,
    Translate
} from '@mui/icons-material';
import { userRequest } from '../../requestMethods';

const LongPostChunkEditor = ({ 
    postId,
    postLanguage,
    chunkDialog,
    onClose,
    onSave,
    showAlert,
    onEditChunk
}) => {
    const [newChunkForm, setNewChunkForm] = useState({
        text: '',
        isTitle: false,
        showInTableOfContents: false
    });
    const [editChunkForm, setEditChunkForm] = useState({
        text: '',
        isTitle: false,
        showInTableOfContents: false
    });
    const [reviewDialog, setReviewDialog] = useState({ open: false, chunk: null });
    const [loading, setLoading] = useState(false);

    const handleAddChunk = async () => {
        if (!newChunkForm.text?.trim()) {
            showAlert('Text content is required', 'error');
            return;
        }

        setLoading(true);
        try {
            const textResponse = await userRequest.post('/text', {
                text: newChunkForm.text.trim(),
                language: postLanguage._id || postLanguage
            });

            await userRequest.post(`/contentLongPost/${postId}/chunks`, {
                text: textResponse.data._id,
                isTitle: newChunkForm.isTitle,
                showInTableOfContents: newChunkForm.showInTableOfContents
            });

            setNewChunkForm({ text: '', isTitle: false, showInTableOfContents: false });
            showAlert('Chunk added successfully', 'success');
            onSave();
            onClose();
        } catch (error) {
            showAlert('Error adding chunk: ' + error.message, 'error');
        } finally {
            setLoading(false);
        }
    };

    const handleSaveEditChunk = async () => {
        if (!editChunkForm.text?.trim()) {
            showAlert('Text content is required', 'error');
            return;
        }

        setLoading(true);
        try {
            // Update text content
            await userRequest.patch(`/text/${chunkDialog.chunk.text._id}`, {
                text: editChunkForm.text.trim()
            });

            // Update chunk metadata
            await userRequest.patch(`/contentLongPost/${postId}/chunks/${chunkDialog.chunk._id}`, {
                isTitle: editChunkForm.isTitle,
                showInTableOfContents: editChunkForm.showInTableOfContents
            });

            setEditChunkForm({ text: '', isTitle: false, showInTableOfContents: false });
            showAlert('Chunk updated successfully', 'success');
            onSave();
            onClose();
        } catch (error) {
            showAlert('Error updating chunk: ' + error.message, 'error');
        } finally {
            setLoading(false);
        }
    };

    const handleReviewChunk = (chunk) => {
        setReviewDialog({ open: true, chunk });
    };

    const handleEditChunk = async (chunk) => {
        try {
            const textResponse = await userRequest.get(`/text/${chunk.text._id}`);
            setEditChunkForm({
                text: textResponse.data.text,
                isTitle: chunk.isTitle,
                showInTableOfContents: chunk.showInTableOfContents
            });
        } catch (error) {
            showAlert('Error loading chunk for editing: ' + error.message, 'error');
        }
    };

    const handleTranslateChunk = (chunk) => {
        if (chunk.text?._id) {
            // Navigate to translation editor
            window.location.href = `/text/translate/${chunk.text._id}`;
        } else {
            showAlert('Cannot open translation editor: Text ID not found', 'error');
        }
    };

    // Set initial form values when dialog opens
    React.useEffect(() => {
        if (chunkDialog.open && chunkDialog.isEdit && chunkDialog.chunk) {
            handleEditChunk(chunkDialog.chunk);
        } else if (chunkDialog.open && chunkDialog.isReview && chunkDialog.chunk) {
            handleReviewChunk(chunkDialog.chunk);
        } else if (chunkDialog.open && !chunkDialog.isEdit) {
            setNewChunkForm({ text: '', isTitle: false, showInTableOfContents: false });
        }
    }, [chunkDialog.open, chunkDialog.isEdit, chunkDialog.isReview, chunkDialog.chunk]);

    return (
        <>
            {/* Add/Edit Chunk Dialog */}
            <Dialog
                open={!!(chunkDialog.open && !chunkDialog.isReview)}
                onClose={onClose}
                maxWidth="md"
                fullWidth
            >
                <DialogTitle>
                    {chunkDialog.isEdit ? 'Edit Chunk' : 'Add New Chunk'}
                </DialogTitle>
                <DialogContent>
                    <TextField
                        fullWidth
                        label="Text Content"
                        multiline
                        rows={10}
                        value={chunkDialog.isEdit ? editChunkForm.text : newChunkForm.text}
                        onChange={(e) => chunkDialog.isEdit
                            ? setEditChunkForm({ ...editChunkForm, text: e.target.value })
                            : setNewChunkForm({ ...newChunkForm, text: e.target.value })
                        }
                        margin="normal"
                    />
                    <FormControlLabel
                        control={
                            <Checkbox
                                checked={chunkDialog.isEdit ? editChunkForm.isTitle : newChunkForm.isTitle}
                                onChange={(e) => chunkDialog.isEdit
                                    ? setEditChunkForm({ ...editChunkForm, isTitle: e.target.checked })
                                    : setNewChunkForm({ ...newChunkForm, isTitle: e.target.checked })
                                }
                            />
                        }
                        label="Is Title Chunk (rendered as heading)"
                    />
                    <FormControlLabel
                        control={
                            <Checkbox
                                checked={chunkDialog.isEdit ? editChunkForm.showInTableOfContents : newChunkForm.showInTableOfContents}
                                onChange={(e) => chunkDialog.isEdit
                                    ? setEditChunkForm({ ...editChunkForm, showInTableOfContents: e.target.checked })
                                    : setNewChunkForm({ ...newChunkForm, showInTableOfContents: e.target.checked })
                                }
                            />
                        }
                        label="Show in Table of Contents"
                    />
                </DialogContent>
                <DialogActions>
                    <Button onClick={onClose} disabled={loading}>Cancel</Button>
                    <Button
                        onClick={chunkDialog.isEdit ? handleSaveEditChunk : handleAddChunk}
                        variant="contained"
                        startIcon={<Save />}
                        disabled={loading || (chunkDialog.isEdit
                            ? (!editChunkForm.text?.trim())
                            : (!newChunkForm.text?.trim())
                        )}
                    >
                        {loading ? 'Saving...' : (chunkDialog.isEdit ? 'Update' : 'Add')}
                    </Button>
                </DialogActions>
            </Dialog>

            {/* Review Dialog */}
            <Dialog
                open={!!(chunkDialog.open && chunkDialog.isReview)}
                onClose={onClose}
                maxWidth="md"
                fullWidth
            >
                <DialogTitle sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                    <Typography variant="h6">Review: {chunkDialog.chunk?.text?.text}</Typography>
                    <IconButton onClick={onClose}>
                        <Close />
                    </IconButton>
                </DialogTitle>
                <DialogContent>
                    {chunkDialog.chunk?.text?.text && (
                        <Box
                            sx={{
                                p: 2,
                                lineHeight: 1.8,
                                fontSize: '1rem',
                                fontFamily: 'Georgia, serif',
                                border: '1px solid #e0e0e0',
                                borderRadius: 1,
                                backgroundColor: '#fafafa'
                            }}
                            dangerouslySetInnerHTML={{
                                __html: chunkDialog.chunk.text.text.replace(/\n/g, '<br/>')
                            }}
                        />
                    )}
                </DialogContent>
                <DialogActions>
                    <Button onClick={() => {
                        onClose();
                        // Trigger edit mode for this chunk
                        if (onEditChunk) {
                            setTimeout(() => {
                                onEditChunk(chunkDialog.chunk);
                            }, 100);
                        }
                    }} startIcon={<Edit />}>
                        Edit
                    </Button>
                    <Button onClick={() => handleTranslateChunk(chunkDialog.chunk)} startIcon={<Translate />}>
                        Translate
                    </Button>
                </DialogActions>
            </Dialog>
        </>
    );
};

export default LongPostChunkEditor; 